import React from 'react';
import useVehicleStats from '../hooks/useVehicleStats';
import CircularGauge from './CircularGauge';
import CellHeatmap from './CellHeatmap';
import './AdvancedAnalysis.css';

const AdvancedAnalysis = ({ vehicleData, isVisible, onClose }) => {
  if (!isVisible) return null;

  const stats = useVehicleStats(vehicleData);
  const { battery, motor } = vehicleData;

  if (!stats) return null;

  return (
    <div className="advanced-analysis-overlay fade-in">
      <div className="analysis-panel slide-in-right">
        {/* Header */}
        <div className="analysis-header">
          <div className="analysis-title">
            <span className="analysis-icon">🔬</span>
            <h2>Advanced analysis</h2>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Content Grid */}
        <div className="analysis-content">
          {/* Circular Gauges Section */}
          <div className="analysis-section">
            <h3 className="section-title">System metrics</h3>
            <div className="gauges-grid">
              <CircularGauge
                value={stats.battery.soc.value}
                max={100}
                label={stats.battery.soc.fullLabel}
                unit={stats.battery.soc.unit}
                color="auto"
                size={140}
                icon={stats.battery.soc.icon}
              />
              <CircularGauge
                value={stats.battery.soh.value}
                max={100}
                label={stats.battery.soh.fullLabel}
                unit={stats.battery.soh.unit}
                color={stats.getHealthColor(stats.battery.soh.value)}
                size={140}
                icon={stats.battery.soh.icon}
              />
              <CircularGauge
                value={stats.battery.temperature.pack}
                max={60}
                min={0}
                label={stats.battery.temperature.fullLabel}
                unit={stats.battery.temperature.unit}
                color="auto"
                size={140}
                icon={stats.battery.temperature.icon}
              />
              <CircularGauge
                value={stats.motor.temperature.value}
                max={120}
                min={0}
                label={stats.motor.temperature.fullLabel}
                unit={stats.motor.temperature.unit}
                color="auto"
                size={140}
                icon={stats.motor.temperature.icon}
              />
            </div>
          </div>

          {/* Battery Cell Heatmap */}
          <div className="analysis-section">
            <h3 className="section-title">Battery cell analysis</h3>
            <div className="heatmap-container">
              <CellHeatmap cells={battery.cells?.voltages?.map((voltage, i) => ({
                voltage,
                temperature: battery.temperature?.cells?.[i] || 28
              })) || []} />
            </div>
            <div className="cell-stats">
              <div className="cell-stat-item">
                <span className="cell-stat-label">MIN VOLTAGE</span>
                <span className="cell-stat-value">
                  {stats.battery.cells.minVoltage?.toFixed(3) || '0.000'}V
                </span>
              </div>
              <div className="cell-stat-item">
                <span className="cell-stat-label">MAX VOLTAGE</span>
                <span className="cell-stat-value">
                  {stats.battery.cells.maxVoltage?.toFixed(3) || '0.000'}V
                </span>
              </div>
              <div className="cell-stat-item">
                <span className="cell-stat-label">DELTA</span>
                <span className="cell-stat-value">
                  {stats.battery.cells.formattedImbalance}
                </span>
              </div>
              <div className="cell-stat-item">
                <span className="cell-stat-label">AVG TEMP</span>
                <span className="cell-stat-value">
                  {battery.temperature?.cells
                    ? (battery.temperature.cells.reduce((sum, t) => sum + t, 0) / battery.temperature.cells.length).toFixed(1)
                    : '0.0'}°C
                </span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="analysis-section">
            <h3 className="section-title">Performance analysis</h3>
            <div className="performance-grid">
              <div className="perf-card">
                <div className="perf-icon">⚡</div>
                <div className="perf-content">
                  <div className="perf-label">Power Output</div>
                  <div className="perf-value">0 kW</div>
                  <div className="perf-status status-idle">IDLE</div>
                </div>
              </div>
              <div className="perf-card">
                <div className="perf-icon">🔋</div>
                <div className="perf-content">
                  <div className="perf-label">Energy Consumption</div>
                  <div className="perf-value">0 kWh/100km</div>
                  <div className="perf-status status-optimal">OPTIMAL</div>
                </div>
              </div>
              <div className="perf-card">
                <div className="perf-icon">♻️</div>
                <div className="perf-content">
                  <div className="perf-label">Regeneration</div>
                  <div className="perf-value">{stats.motor.efficiency.formatted}</div>
                  <div className="perf-status status-good">GOOD</div>
                </div>
              </div>
              <div className="perf-card">
                <div className="perf-icon">📊</div>
                <div className="perf-content">
                  <div className="perf-label">System Efficiency</div>
                  <div className="perf-value">92.3%</div>
                  <div className="perf-status status-excellent">EXCELLENT</div>
                </div>
              </div>
            </div>
          </div>

          {/* Battery Chemistry Details */}
          <div className="analysis-section">
            <h3 className="section-title">Battery details</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">CHEMISTRY</span>
                <span className="detail-value">{battery.chemistry}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">CAPACITY</span>
                <span className="detail-value">{stats.battery.capacity.formatted}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">PACK VOLTAGE</span>
                <span className="detail-value">{stats.battery.voltage.formatted}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">CURRENT</span>
                <span className="detail-value">{stats.battery.current.formatted}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">TOTAL CELLS</span>
                <span className="detail-value">{stats.battery.cells.count}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">MODULES</span>
                <span className="detail-value">{battery.modules || 10}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">COOLING</span>
                <span className="detail-value">Liquid</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">BMS VERSION</span>
                <span className="detail-value">v3.2.1</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalysis;
